import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useHydrateCartCount } from '../lib/cart/useHydrateCartCount'
import { useRfqBundle } from '../lib/rfq/rfq-bundle-context'
import { useHydrateRfqCount } from '../lib/rfq/useHydrateRfqCount'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { useToast } from './ui/ToastProvider'

/**
 * Code-review C2.2 (UID-552) — surfaces a FAILED RFQ-bundle durability write
 * to the buyer, through the app's established transient-failure convention
 * (an error `showToast`, exactly as `RfqReviewPage.handleShare` /
 * `handleCopyShareLink` surface theirs).
 *
 * Lives HERE rather than in the store itself for a layering reason: nothing
 * under `lib/` imports from `components/` in this codebase, and
 * `rfq-bundle-context.tsx` must stay UI-free. So the store reports the
 * failure as data (`persistError`) and this shell — already the one place
 * that wires app-wide cross-cutting concerns onto every authenticated screen
 * (`useHydrateCartCount`, `useHydrateRfqCount`) — renders it.
 *
 * Announces once per transition INTO the failed state, not once per write:
 * a quota-exceeded snapshot re-fails on every subsequent bundle change, and
 * re-toasting each time would bury the rest of the UI in duplicates.
 */
function useRfqBundlePersistFailureToast() {
  const { persistError } = useRfqBundle()
  const { showToast } = useToast()
  const announcedRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (persistError !== undefined && persistError !== announcedRef.current) {
      showToast(persistError, 'error')
    }
    announcedRef.current = persistError
  }, [persistError, showToast])
}

/**
 * App shell: sidebar + top bar chrome, routed <main> (UID-27 — replaces
 * the pre-UID-27 top-nav-only shell; see docs/design/README.md's
 * "Reconciliation summary").
 *
 * Focus management (accessibility-wcag skill): on every route change, focus
 * moves to <main> so screen-reader/keyboard users land at the new content
 * instead of staying anchored to a now-stale nav item — unchanged from the
 * pre-UID-27 shell, just relocated unchanged into this file. Also resets
 * scroll to top on navigation (interactions.md §1's "`go(screen)`'s
 * scroll-to-top maps to a route-change effect" — React Router doesn't
 * auto-scroll-restore by default).
 *
 * UID-423 (ADR-0127 §H) — also hydrates the top-bar cart-count badge:
 * `<AppShell>` sits inside both `<AuthProvider>` and `<CartCountProvider>`
 * (`App.tsx`), so it's the one place that can fire a single, authenticated-
 * only `GET /cart` per session without adding that call to every individual
 * screen. See `useHydrateCartCount`'s own doc comment for the full
 * gating/error posture.
 *
 * UID-529 (ADR-0167) — also hydrates the Sidebar "RFQ" nav-badge total-count
 * (`useHydrateRfqCount`), same seam/rationale as the cart hydrate above,
 * except it refetches on every route change rather than once per session —
 * see that hook's own doc comment for why.
 */
export function AppShell() {
  useHydrateCartCount()
  useHydrateRfqCount()
  useRfqBundlePersistFailureToast()
  const location = useLocation()
  const mainRef = useRef<HTMLElement | null>(null)
  const previousPathnameRef = useRef(location.pathname)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  // Mobile off-canvas drawer (≤900px): the sidebar is translated off-screen by
  // default and slid in over a scrim when this is true. Only relevant on narrow
  // viewports — on desktop the sidebar is always in-flow and this stays false.
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) {
      return
    }
    previousPathnameRef.current = location.pathname
    // Navigating always dismisses the mobile drawer — the buyer picked a
    // destination, so the nav that covered the content must get out of the way.
    setMobileNavOpen(false)
    mainRef.current?.focus()
    try {
      // jsdom (the test environment) stubs window.scrollTo with a function
      // that logs "not implemented" rather than throwing — swallowed here
      // so component tests don't emit a spurious console error; real
      // browsers implement this normally.
      window.scrollTo(0, 0)
    } catch {
      // no-op — see above.
    }
  })

  // Escape closes the mobile drawer (matches every other overlay in the app —
  // account menu, modals — so the dismiss gesture is consistent).
  useEffect(() => {
    if (!mobileNavOpen) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMobileNavOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mobileNavOpen])

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <div className="shell">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
          mobileOpen={mobileNavOpen}
          onCloseMobile={() => setMobileNavOpen(false)}
        />
        {/* Scrim behind the drawer — click to dismiss. Only rendered while the
            drawer is open; CSS (shell.css) shows it only at ≤900px. */}
        {mobileNavOpen && (
          <button
            type="button"
            className="sidebar-scrim"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
          />
        )}
        <div className="shell-content">
          <TopBar onOpenMobileNav={() => setMobileNavOpen(true)} />
          <main id="main-content" className="app-main" ref={mainRef} tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
    </>
  )
}
